// The approval decision, wherever an approval shows (M3-08 Approvals; AGT-UI-2: the chat card and
// the inbox row; research rec 9, 23): Approve in one tap, Edit and approve (title, summary and the
// change as JSON — a held agent post is edited as its text: what is saved is what executes),
// Reject with the reason the contract requires. One component, so the Agents queue, the approval
// page, a held agent post in Chat and the inbox decide the same way and refresh the same things:
// Agents' runs and approvals (and the sidebar's count), the chat card and the inbox.
// Phase 1 inc 2: an approved change executes. The decision answers with its execution outcome
// (`execution_*`): a change that could not be applied says so ("Approved · not applied: …"), here
// and in `decisionToast`, never a plain "Approved".
import {
  bodyTextOf,
  editHeldPost,
  heldPostParts,
  type PostPart,
} from "@companyos/domain-work/chat";
import {
  Button,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, PencilIcon, Undo2Icon, XIcon } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { ApprovalRow } from "../../api/types";
import { chatKeys } from "../chat/chat-queries";
import { problemText } from "../de/de-status";
import { agentKeys, approvalQuery, invalidateDecided } from "./agent-queries";
import { executionOf } from "./approvals-screen";
import { chosenLabel } from "./escalation-card";

type Size = "xs" | "sm" | "default" | "lg";

/** A decision is settled once rejected or its change applied (not applying, waiting or failed). */
export function decisionSettled(d: Pick<ApprovalRow, "state" | "execution_state">): boolean {
  return d.state === "rejected" || d.execution_state === "applied";
}

/**
 * What became of an approval decided here — rejected, applied, applying, or not applied with the
 * reason — read live (a decision or the approval's change pings refetch it), starting from the
 * decision's answer, so a change that fails after the decision is still reported where it was
 * decided. `onSettled` runs once when it settles (an inbox row is then done).
 */
export function DecisionOutcome({
  api,
  co,
  approval: decided,
  title,
  onSettled,
}: {
  api: Api;
  co: string;
  approval: ApprovalRow;
  /** Name the approval (a list of decisions); a row that already names it leaves it out. */
  title?: boolean;
  onSettled?: () => void;
}) {
  // The decision's answer is a snapshot: stale at once, so the mount reads the approval again.
  const live = useQuery({
    ...approvalQuery(api, co, decided.id),
    // the decider saw it and it is decided; the read refetches at once (stale at 0)
    initialData: { ...decided, can_view: true, can_decide: false },
    initialDataUpdatedAt: 0,
  });
  const a = live.data ?? decided;
  const x = executionOf(a);
  const outcome = a.state === "rejected" ? "Rejected" : (x?.text ?? "Approved");
  // CORE-CARDS: a card decided by an option says which
  const chose = chosenLabel(a);
  const text = chose ? `Chose ${chose} · ${outcome}` : outcome;
  const settled = decisionSettled(a);
  const told = useRef(false);
  useEffect(() => {
    if (!settled || told.current) return;
    told.current = true;
    onSettled?.();
  }, [settled, onSettled]);
  return (
    <p
      role={x?.tone === "danger" ? "alert" : "status"}
      data-slot="approval-outcome"
      data-approval={a.id}
      data-execution-state={a.execution_state ?? undefined}
      className={cn(
        "mt-1 text-xs",
        x?.tone === "danger" ? "text-destructive" : "text-muted-foreground",
      )}
    >
      {title ? <span className="font-medium text-foreground">{a.proposal.title}: </span> : null}
      {text}
    </p>
  );
}

/**
 * The toast after a decision: rejected; approved and applied (`applied`, the caller's wording);
 * approved and still applying or waiting (said so); approved but not applied (the server's
 * reason, as an error). An approval whose change has not happened never reads as plain success.
 */
export function decisionToast(
  d: ApprovalRow,
  applied = "Approved",
): { title: string; description?: string; type: "success" | "error" } {
  if (d.state === "rejected") return { title: "Rejected", type: "success" };
  const x = executionOf(d);
  if (x && x.tone === "danger")
    return { title: "Approved · not applied", description: x.text, type: "error" };
  const title = d.state === "edited" ? "Approved with edits" : "Approved";
  if (x?.tone !== "success")
    return { title, description: x?.text ?? "Approved · applying…", type: "success" };
  return { title: d.state === "edited" ? `${applied} (with edits)` : applied, type: "success" };
}

/**
 * Approve / Edit / Reject for a pending approval. `onDecided` gets the decided approval (a chat
 * card or inbox row turns into its outcome at once). `editor` replaces the built-in edit dialog
 * (a KPI value review edits its number, kpi-review.tsx).
 */
export function ApprovalActions({
  api,
  co,
  approval: a,
  size = "default",
  edit = true,
  approveButton = true,
  sendBack = false,
  editor,
  onDecided,
}: {
  api: Api;
  co: string;
  approval: ApprovalRow;
  size?: Size;
  /** Offer "Edit and approve". */
  edit?: boolean;
  /** Offer one-tap Approve (a batch review applies from its rows instead). */
  approveButton?: boolean;
  /** B2-GATES: offer "Send back" (a rejection with a note that wakes the DE). */
  sendBack?: boolean;
  editor?: ReactNode;
  onDecided?: (decided: ApprovalRow) => void;
}) {
  const qc = useQueryClient();
  const [outcome, setOutcome] = useState<ApprovalRow | null>(null);
  const decided = (d: ApprovalRow) => {
    qc.setQueryData(agentKeys.approval(co, d.id), d);
    qc.setQueryData(chatKeys.approval(co, d.id), d);
    setOutcome(d);
    onDecided?.(d);
  };
  const approve = useMutation({
    mutationFn: () => api.client.approvals.approve({ params: { co, id: a.id } }),
    onSuccess: decided,
    onSettled: () => invalidateDecided(qc, co),
  });
  const failed = outcome ? executionOf(outcome) : null;
  return (
    <span className="flex flex-col gap-1.5" data-slot="approval-actions">
      <span className="flex flex-wrap gap-1.5">
        {approveButton ? (
          <Button
            size={size}
            disabled={approve.isPending}
            onClick={() => approve.mutate()}
            aria-label={`Approve: ${a.proposal.title}`}
          >
            <CheckIcon aria-hidden="true" />
            {approve.isPending ? "Approving…" : "Approve"}
          </Button>
        ) : null}
        {editor ??
          (edit ? (
            <EditDialog api={api} co={co} approval={a} size={size} onDone={decided} />
          ) : null)}
        {sendBack ? (
          <SendBackDialog api={api} co={co} approval={a} size={size} onDone={decided} />
        ) : null}
        <RejectDialog api={api} co={co} approval={a} size={size} onDone={decided} />
      </span>
      {approve.isError ? (
        <span role="alert" className="text-sm text-destructive">
          {problemText(approve.error, "The approval could not be approved.")}
        </span>
      ) : failed && failed.tone === "danger" ? (
        <span role="alert" className="text-sm text-destructive" data-slot="approval-not-applied">
          {failed.text}
        </span>
      ) : null}
    </span>
  );
}

/** Reject with a reason (required by the contract). */
export function RejectDialog({
  api,
  co,
  approval: a,
  size = "default",
  onDone,
}: {
  api: Api;
  co: string;
  approval: ApprovalRow;
  size?: Size;
  onDone?: (d: ApprovalRow) => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const id = useId();
  const reject = useMutation({
    mutationFn: () =>
      api.client.approvals.reject({ params: { co, id: a.id }, body: { reason: reason.trim() } }),
    onSuccess: (d) => {
      setOpen(false);
      qc.setQueryData(agentKeys.approval(co, d.id), d);
      qc.setQueryData(chatKeys.approval(co, d.id), d);
      onDone?.(d);
    },
    onSettled: () => invalidateDecided(qc, co),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="outline"
        size={size}
        onClick={() => setOpen(true)}
        aria-label={`Reject: ${a.proposal.title}`}
      >
        <XIcon aria-hidden="true" />
        Reject
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject “{a.proposal.title}”?</DialogTitle>
          <DialogDescription>
            Nothing is sent or changed. The digital employee gets your reason back.
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor={id}>Reason</FieldLabel>
          <Textarea
            id={id}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Wait for the final job description."
          />
        </Field>
        {reject.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(reject.error, "The approval could not be rejected.")}
          </p>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button
            variant="destructive"
            disabled={!reason.trim() || reject.isPending}
            onClick={() => reject.mutate()}
          >
            {reject.isPending ? "Rejecting…" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * B2-GATES: "Send back with a note" — nothing is applied; the note goes to the DE, which is woken
 * to rework it (a named decision, apart from a plain reject).
 */
export function SendBackDialog({
  api,
  co,
  approval: a,
  size = "default",
  onDone,
}: {
  api: Api;
  co: string;
  approval: ApprovalRow;
  size?: Size;
  onDone?: (d: ApprovalRow) => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const id = useId();
  const send = useMutation({
    mutationFn: () =>
      api.client.approvals.sendBack({ params: { co, id: a.id }, body: { note: note.trim() } }),
    onSuccess: (d) => {
      setOpen(false);
      qc.setQueryData(agentKeys.approval(co, d.id), d);
      qc.setQueryData(chatKeys.approval(co, d.id), d);
      onDone?.(d);
    },
    onSettled: () => invalidateDecided(qc, co),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="outline"
        size={size}
        onClick={() => setOpen(true)}
        aria-label={`Send back: ${a.proposal.title}`}
      >
        <Undo2Icon aria-hidden="true" />
        Send back
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send back “{a.proposal.title}”</DialogTitle>
          <DialogDescription>
            Nothing changes. The DE gets your note and reworks it.
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor={id}>Note</FieldLabel>
          <Textarea
            id={id}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Shorter hooks, and drop the two about pricing."
          />
        </Field>
        {send.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(send.error, "The approval could not be sent back.")}
          </p>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button disabled={!note.trim() || send.isPending} onClick={() => send.mutate()}>
            {send.isPending ? "Sending…" : "Send back"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A held post's text (the thread gate's `change.body`, BlockNote), or null for other kinds. */
export function heldPostText(a: Pick<ApprovalRow, "kind" | "proposal">): string | null {
  if (a.kind !== "message") return null;
  const body = (a.proposal.change as { body?: unknown }).body;
  return Array.isArray(body) ? bodyTextOf(body) : null;
}

/** The thread gate's summary is the post's excerpt (thread-gate.ts `excerpt`); it follows an edit. */
const excerpt = (t: string) => (t.length > 280 ? `${t.slice(0, 279)}…` : t);

const INDENTS = ["ms-0", "ms-5", "ms-10", "ms-15"] as const;

const PART_LABELS: Record<string, string> = {
  paragraph: "Paragraph",
  heading: "Heading",
  bulletListItem: "List item",
  numberedListItem: "Numbered item",
  checkListItem: "Checklist item",
  quote: "Quote",
  codeBlock: "Code",
};

/**
 * A held post's blocks in the editor: plain text blocks as text fields (exact edits), the rest
 * (styled text, links, mentions, other block types) shown read-only. One editable block and
 * nothing else reads simply as "Message".
 */
function HeldPostFields({
  parts,
  edits,
  onEdit,
}: {
  parts: PostPart[];
  edits: ReadonlyMap<string, string>;
  onEdit: (key: string, text: string) => void;
}) {
  const single = parts.length === 1 && parts[0]?.editable;
  const locked = parts.some((p) => !p.editable);
  let n = 0;
  return (
    <fieldset className="m-0 flex min-w-0 flex-col gap-2 border-0 p-0">
      <legend className="mb-1 text-sm font-medium">Message</legend>
      {parts.map((p) => {
        const label = single
          ? "Message"
          : `${PART_LABELS[p.type] ?? "Block"} ${++n}${p.editable ? "" : " (read-only)"}`;
        // Nested blocks (list children) indent with their depth.
        const indent = INDENTS[Math.min(p.depth, INDENTS.length - 1)];
        if (!p.editable)
          return (
            <blockquote
              key={p.key}
              aria-label={label}
              className={`my-0 me-0 ${indent} whitespace-pre-wrap border-l-2 border-border pl-2 text-sm text-muted-foreground`}
            >
              {p.text || "(no text)"}
            </blockquote>
          );
        return (
          <Textarea
            key={p.key}
            aria-label={label}
            value={edits.get(p.key) ?? p.text}
            onChange={(e) => onEdit(p.key, e.target.value)}
            className={`${indent} ${p.type === "codeBlock" ? "min-h-24 font-mono text-xs" : "min-h-16"}`}
          />
        );
      })}
      <FieldDescription>
        Posted as the agent, in the same thread, once you approve. Only the text you change is
        changed; the rest goes out exactly as the agent wrote it.
        {locked
          ? " Formatted text, links and mentions are shown read-only: reject with a note to have them changed."
          : ""}
      </FieldDescription>
    </fieldset>
  );
}

/**
 * Approve with edits: title, summary and the change (JSON) as the approver wants them, and an
 * optional note. A held agent post (`message`) is edited block by block (`editHeldPost`): only
 * the blocks the approver changed are rebuilt, everything else is the agent's own document (its
 * mentions stay those it asked for).
 */
function EditDialog({
  api,
  co,
  approval: a,
  size,
  onDone,
}: {
  api: Api;
  co: string;
  approval: ApprovalRow;
  size: Size;
  onDone: (d: ApprovalRow) => void;
}) {
  const qc = useQueryClient();
  const post = heldPostText(a);
  const heldBody = (a.proposal.change as { body?: unknown }).body;
  const doc: unknown[] = post !== null && Array.isArray(heldBody) ? heldBody : [];
  const parts = heldPostParts(doc);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(a.proposal.title);
  const [summary, setSummary] = useState(a.proposal.summary ?? "");
  const [edits, setEdits] = useState<ReadonlyMap<string, string>>(new Map());
  const [change, setChange] = useState(JSON.stringify(a.proposal.change, null, 2));
  const [note, setNote] = useState("");
  const [bad, setBad] = useState<string | null>(null);
  const ids = { title: useId(), summary: useId(), change: useId(), note: useId() };
  const save = useMutation({
    mutationFn: ({ parsed, about }: { parsed: Record<string, unknown>; about: string }) =>
      api.client.approvals.edit({
        params: { co, id: a.id },
        body: {
          proposal: {
            title: title.trim(),
            ...(about.trim() ? { summary: about.trim() } : {}),
            change: parsed,
          },
          ...(note.trim() ? { reason: note.trim() } : {}),
        },
      }),
    onSuccess: (d) => {
      setOpen(false);
      onDone(d);
    },
    onSettled: () => invalidateDecided(qc, co),
  });
  const submit = () => {
    if (post !== null) {
      const body = editHeldPost(doc, edits);
      // Nothing changed (a note only): the agent's own proposal goes out exactly as it was.
      if (body.every((b, i) => b === doc[i])) {
        setBad(null);
        return save.mutate({ parsed: a.proposal.change, about: a.proposal.summary ?? "" });
      }
      if (!bodyTextOf(body).trim()) return setBad("The message must not be empty.");
      setBad(null);
      save.mutate({ parsed: { ...a.proposal.change, body }, about: excerpt(bodyTextOf(body)) });
      return;
    }
    try {
      const parsed = JSON.parse(change) as unknown;
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
        throw new Error("not an object");
      setBad(null);
      save.mutate({ parsed: parsed as Record<string, unknown>, about: summary });
    } catch {
      setBad('The change must be a JSON object, like {"status": "Done"}.');
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="secondary" size={size} onClick={() => setOpen(true)}>
        <PencilIcon aria-hidden="true" />
        {size === "lg" ? "Edit and approve" : "Edit"}
      </Button>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit and approve</DialogTitle>
          <DialogDescription>
            What you save here is what executes; the original proposal is kept beside it.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {post !== null ? (
            <HeldPostFields
              parts={parts}
              edits={edits}
              onEdit={(key, t) => setEdits((m) => new Map(m).set(key, t))}
            />
          ) : (
            <>
              <Field>
                <FieldLabel htmlFor={ids.title}>Title</FieldLabel>
                <Input id={ids.title} value={title} onChange={(e) => setTitle(e.target.value)} />
              </Field>
              <Field>
                <FieldLabel htmlFor={ids.summary}>Summary</FieldLabel>
                <Textarea
                  id={ids.summary}
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor={ids.change}>Change</FieldLabel>
                <Textarea
                  id={ids.change}
                  value={change}
                  onChange={(e) => setChange(e.target.value)}
                  className="min-h-40 font-mono text-xs"
                />
                <FieldDescription>The fields the proposal changes, as JSON.</FieldDescription>
              </Field>
            </>
          )}
          <Field>
            <FieldLabel htmlFor={ids.note}>Note (optional)</FieldLabel>
            <Textarea
              id={ids.note}
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
        </div>
        {bad || save.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {bad ?? problemText(save.error, "The approval could not be saved.")}
          </p>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button disabled={!title.trim() || save.isPending} onClick={submit}>
            {save.isPending ? "Approving…" : "Approve with edits"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
